// GAEB position kinds and offer totals, client twin of utils/gaeb/calc.js (live totals in the
// offer editor and the invoice dialog; the server stays authoritative). Keep everything below
// the header identical to the server file: test/gaeb/calc.test.js compares both.

const ITEM_KINDS = ["normal", "base", "alternative", "markup"];
const PROVISIONAL = ["", "with_total", "without_total"];

const roundHalfUp = value => {
  const sign = value < 0 ? -1 : 1;
  return sign * Math.round(Number(Math.abs(value).toPrecision(15)));
};

const euroToCents = value => {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? roundHalfUp(n * 100) : 0;
};

/** Net cents of an offer item: quantity × unit price, rounded half up once. */
const itemNet = item => roundHalfUp(euroToCents(item?.price) * Number(item?.quantity || 0));

const gaebOf = item => (item && item.gaeb && typeof item.gaeb === "object" ? item.gaeb : null);

// Alternatives belong to their Grundposition through the group number within one LV-Bereich
const groupKey = g => `${(g.titlePath || []).join("|")}#${g.altGroup}`;

const hasGroup = g => g.altGroup !== null && g.altGroup !== undefined && g.altGroup !== "";

/** For each item: does its net count into the offer total? */
const countingFlags = items => {
  const list = items || [];
  const ordered = new Set();
  list.forEach(item => {
    const g = gaebOf(item);
    if (g && g.kind === "alternative" && g.awarded && hasGroup(g)) ordered.add(groupKey(g));
  });
  return list.map(item => {
    const g = gaebOf(item);
    if (!g) return true;
    if (g.notApplicable) return false;
    if (g.kind === "base" && hasGroup(g) && ordered.has(groupKey(g))) return false;
    if (g.kind === "alternative" || g.provisional) return Boolean(g.awarded);
    return true;
  });
};

/**
 * Is the Gesamtbetrag printed (and exported as IT)? Counted positions always; a Bedarfsposition
 * "mit GB" and a Grundposition also when they do not count; Alternativpositionen and
 * Bedarfspositionen "ohne GB" show "Nur Einh.-Preis".
 */
const showsTotal = (item, counts) => {
  const g = gaebOf(item);
  if (!g || counts) return true;
  if (g.notApplicable || g.kind === "alternative") return false;
  if (g.kind === "base") return true;
  return g.provisional === "with_total";
};

/** Short codes describing a position (the UI and the PDF turn them into labels). */
const tagsOf = item => {
  const g = gaebOf(item);
  if (!g) return [];
  const tags = [];
  if (g.kind === "base") tags.push("base");
  if (g.kind === "alternative") tags.push("alternative");
  if (g.kind === "markup") tags.push("markup");
  if (g.provisional === "with_total") tags.push("provisional_with_total");
  if (g.provisional === "without_total") tags.push("provisional_without_total");
  if (g.lumpSum) tags.push("lump_sum");
  if (g.hourly) tags.push("hourly");
  if (g.surcharge) tags.push("surcharge");
  if (g.freeQty) tags.push("free_quantity");
  if (g.notApplicable) tags.push("not_applicable");
  if (g.awarded && (g.kind === "alternative" || g.provisional)) tags.push("awarded");
  return tags;
};

/**
 * Totals of an offer's items: net (counted positions only), tax, gross, and `optionalNet` (what
 * the Bedarfs-/Alternativpositionen would add), plus per item { net, counts, showsTotal }.
 */
const offerTotals = (items, taxRate = 19) => {
  const flags = countingFlags(items);
  const positions = (items || []).map((item, i) => ({
    net: itemNet(item),
    counts: flags[i],
    showsTotal: showsTotal(item, flags[i])
  }));
  const net = positions.reduce((s, p) => s + (p.counts ? p.net : 0), 0);
  const optionalNet = positions.reduce((s, p) => s + (p.counts ? 0 : p.net), 0);
  const rate = Number.isFinite(Number(taxRate)) ? Number(taxRate) : 19;
  const tax = roundHalfUp((net * rate) / 100);
  return { net, tax, gross: net + tax, optionalNet, positions };
};

/**
 * Title headers to show before each item: the OZ of every LV-Bereich the item enters compared
 * with the item before it (items keep their `gaeb.titlePath`, outermost first).
 */
const titleHeaders = items => {
  let previous = [];
  return (items || []).map(item => {
    const path = gaebOf(item)?.titlePath || [];
    let same = 0;
    while (same < path.length && same < previous.length && path[same] === previous[same]) same += 1;
    previous = path;
    return path.slice(same);
  });
};

export {
  ITEM_KINDS,
  PROVISIONAL,
  roundHalfUp,
  euroToCents,
  itemNet,
  countingFlags,
  showsTotal,
  tagsOf,
  offerTotals,
  titleHeaders
};
